import { useState } from 'react'
import { PlusIcon } from '@/components/Icons'
import { SiteHeader } from '@/components/site-header'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { DataTable } from '@/components/data-table'
import {
  getCategoryColumns,
  type Category,
} from '@/components/category/category-columns'
import { getTagColumns, type Tag } from '@/components/tag/tag-columns'
import AddTagDrawer, { type TagFormData } from '@/components/tag/AddTagDrawer'
import AddCategoryDrawer from '@/components/category/AddCategoryDrawer'
import ViewCategoryDrawer from '@/components/category/ViewCategoryDrawer'
import type { SortingState } from '@tanstack/react-table'
import { showErrorToast, showSuccessToast } from '@/components/ui/toast'
import { deleteCategory, fetchCategory } from '@/api/categoryApi'
import useDebounce from '@/hooks/use-debounce'
import { CATEGORY_QUERY_KEYS, TAG_QUERY_KEYS } from '@/api/queryKeys'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { Category as Categories } from '@/schemas/category.schema'
import { transformCategoryData, transformTagData } from '@/utils/helper'
import axios from 'axios'
import { deleteTag, fetchTag } from '@/api/tagApi'
import type { Tags } from '@/schemas/tag.schema'

function CategoryManagement() {
  const [searchQuery, setSearchQuery] = useState('')
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [sorting, setSorting] = useState<SortingState>([])
  const [activeTab, setActiveTab] = useState('categories')
  const [isAddCategoryOpen, setIsAddCategoryOpen] = useState(false)
  const [isAddTagOpen, setIsAddTagOpen] = useState(false)
  const [isViewCategoryOpen, setIsViewCategoryOpen] = useState(false)
  const queryClient = useQueryClient()

  // State for pagination
  const [pagination, setPagination] = useState({
    pageIndex: currentPage - 1,
    pageSize: pageSize,
  })

  const debouncedSearchTerm = useDebounce(searchQuery, 500)

  const {
    data: categoryData,
    isLoading,
    error,
  } = useQuery({
    queryKey: [
      CATEGORY_QUERY_KEYS.all,
      currentPage,
      pageSize,
      debouncedSearchTerm,
      sorting,
    ],
    queryFn: () =>
      fetchCategory({
        limit: pageSize,
        page: currentPage,
        search: debouncedSearchTerm || undefined,
        sort: sorting,
      }),
  })

  const {
    data: tagData,
    isLoading: isTagsLoading,
    error: tagsError,
  } = useQuery({
    queryKey: [
      TAG_QUERY_KEYS.all,
      currentPage,
      pageSize,
      debouncedSearchTerm,
      sorting,
    ],
    queryFn: () =>
      fetchTag({
        limit: pageSize,
        page: currentPage,
        search: debouncedSearchTerm || undefined,
        sort: sorting,
      }),
  })

  const { mutate: deleteCategoryMutation } = useMutation({
    mutationFn: deleteCategory,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [CATEGORY_QUERY_KEYS.all] })

      showSuccessToast(
        'Category deleted.',
        'The category has been successfully removed from the system.'
      )
    },
    onError: error => {
      if (axios.isAxiosError(error)) {
        const message =
          error?.response?.data?.message || 'Failed to create category'
        showErrorToast('Category delete failed', message)
      }
    },
  })

  const { mutate: deleteTagMutation } = useMutation({
    mutationFn: deleteTag,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [TAG_QUERY_KEYS.all] })

      showSuccessToast(
        'Tag deleted.',
        'The tag has been successfully removed from the system.'
      )
    },
    onError: error => {
      if (axios.isAxiosError(error)) {
        const message = error?.response?.data?.message || 'Failed to delete tag'
        showErrorToast('Tag delete failed', message)
      }
    },
  })

  const handleSearchChange = (value: string) => {
    setSearchQuery(value)
    setCurrentPage(1) // Reset to first page when searching
  }

  const handleAddCategory = () => {
    setIsAddCategoryOpen(true)
  }

  const handleAddTag = () => {
    setIsAddTagOpen(true)
  }

  const handleTagSubmit = (tagData: TagFormData) => {
    // Add tag creation logic here
    console.log('Tag submitted:', tagData)
    showSuccessToast(
      'Tag added successfully.',
      'The new tag has been created and is now available for event organizers to use.'
    )
  }

  const handleEdit = (category: Category) => {
    console.log('Edit category:', category)
  }

  const handleDelete = (category: Category) => {
    try {
      deleteCategoryMutation(category.id)
    } catch (error) {
      console.error('Event creation error:', error)
      showErrorToast('Event Creation Failed', 'Failed to create event')
    }
  }

  const handleTagDelete = (tag: Tag) => {
    try {
      deleteTagMutation(tag.id)
    } catch (error) {
      console.error('Event deletion error:', error)
      showErrorToast('Event deletion Failed', 'Failed to delete tag')
    }
  }

  const handleViewEvents = () => {
    setIsViewCategoryOpen(true)
  }

  const handlePageChange = (page: number) => {
    setCurrentPage(page)
  }

  const handlePageSizeChange = (size: number) => {
    setPageSize(size)
    setCurrentPage(1) // Reset to first page when changing page size
  }

  const totalItems = categoryData?.data?.totalCount || 0
  const totalPages =
    categoryData?.data?.totalPages || Math.ceil(totalItems / pageSize)

  const totalTagItems = tagData?.data?.totalCount || 0
  const totalTagPages =
    tagData?.data?.totalPages || Math.ceil(totalItems / pageSize)

  // Get columns with handlers
  const categoryColumns = getCategoryColumns({
    onEdit: handleEdit,
    onDelete: handleDelete,
    onViewEvents: handleViewEvents,
  })

  const tagColumns = getTagColumns({
    onDelete: handleTagDelete,
  })

  // Determine current button text and handler based on active tab
  const isTagsTab = activeTab === 'tags'
  const currentButtonText = isTagsTab ? 'Add Tag' : 'Add Category'
  const currentButtonHandler = isTagsTab ? handleAddTag : handleAddCategory

  const rawCategories: Categories[] = categoryData?.data?.result || []
  const displayCategories: Categories[] = rawCategories.map(
    transformCategoryData
  )

  const tawTags: Tags[] = tagData?.data?.result || []
  const displayTags: Tags[] = tawTags.map(transformTagData)

  return (
    <main
      className="@container/main flex flex-1 flex-col"
      id="main-content"
      tabIndex={-1}
      aria-label="Category Management Main Content"
    >
      <section
        aria-label="Category Management Header and Actions"
        className="flex flex-col gap-4 md:gap-6"
      >
        <SiteHeader
          title="Category Management"
          subtitle="Create and organize event categories to make it easier for users to discover relevant events."
          searchPlaceholder="Search"
          buttonText={currentButtonText}
          searchValue={searchQuery}
          buttonIcon={<PlusIcon className="h-5 w-5" aria-hidden="true" />}
          onButtonClick={currentButtonHandler}
          onSearchChange={handleSearchChange}
        />
      </section>

      <section aria-label="Categories & Tags" className="mt-2">
        <div className="p-4 lg:p-6">
          <div className="flex gap-4 items-center justify-start mb-4 lg:mb-6">
            <h2 className="text-xl font-bold text-ss-neutral-950 leading-7">
              Categories & Tags
            </h2>
          </div>

          <Tabs
            value={activeTab}
            onValueChange={setActiveTab}
            className="w-full"
          >
            {/* Custom styled round tabs to match Figma design */}
            <TabsList>
              <TabsTrigger value="categories">Categories</TabsTrigger>
              <TabsTrigger value="tags">Tags</TabsTrigger>
            </TabsList>

            <TabsContent value="categories" className="mt-0">
              <DataTable<Category, unknown>
                parentClassName="lg:mx-0"
                data={displayCategories}
                columns={categoryColumns}
                sorting={sorting}
                setSorting={setSorting}
                pagination={pagination}
                setPagination={setPagination}
                showFilter={false}
                showExport={false}
                isLoading={isLoading}
                error={error?.message || null}
                totalItems={totalItems}
                currentPage={currentPage}
                pageSize={pageSize}
                totalPages={totalPages}
                onPageChange={handlePageChange}
                onPageSizeChange={handlePageSizeChange}
                emptyState={{
                  image: '/auth/empty-categorylist.svg',
                  title: 'No categories found',
                  subtitle:
                    'You haven’t created any categories yet. Start by adding a new category to organize your events and make them easier to manage.',
                }}
              />
            </TabsContent>

            <TabsContent value="tags" className="mt-0">
              <DataTable<Tag, unknown>
                parentClassName="lg:mx-0"
                data={displayTags}
                columns={tagColumns}
                sorting={sorting}
                setSorting={setSorting}
                pagination={pagination}
                setPagination={setPagination}
                showFilter={false}
                showExport={false}
                isLoading={isTagsLoading}
                error={tagsError?.message || null}
                totalItems={totalTagItems}
                currentPage={currentPage}
                pageSize={pageSize}
                totalPages={totalTagPages}
                onPageChange={handlePageChange}
                onPageSizeChange={handlePageSizeChange}
                emptyState={{
                  image: '/auth/empty-categorylist.svg',
                  title: 'No tags found',
                  subtitle:
                    'You haven’t created any tags yet. Start by adding a new tags to organize your events and make them easier to manage.',
                }}
              />
            </TabsContent>
          </Tabs>
        </div>
      </section>

      {/* Add Category Dialog */}
      <AddCategoryDrawer
        open={isAddCategoryOpen}
        onOpenChange={setIsAddCategoryOpen}
      />

      {/* Add Tag Dialog */}
      <AddTagDrawer
        open={isAddTagOpen}
        onOpenChange={setIsAddTagOpen}
        onSubmit={handleTagSubmit}
      />

      {/* View Category Events Drawer */}
      <ViewCategoryDrawer
        open={isViewCategoryOpen}
        onOpenChange={setIsViewCategoryOpen}
      />
    </main>
  )
}

export default CategoryManagement
